"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Script from "next/script";
import { NewButton } from "./ui/NewButton";
import { TrackingId } from "@/lib/tracking";

// Global state management for Calendly loading
let globalCalendlyLoaded = false;
const calendlyListeners = new Set<() => void>();

const notifyCalendlyLoaded = () => {
  globalCalendlyLoaded = true;
  calendlyListeners.forEach(listener => listener());
};

const addCalendlyListener = (listener: () => void) => {
  calendlyListeners.add(listener);
  return () => calendlyListeners.delete(listener);
};

/** Calendly uses "200+" while our form uses "201+" */
const mapScreenCountForCalendly = (screenCount: string) =>
  screenCount === "201+" ? "200+" : screenCount;

interface CalendlyAutoPopulate {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  company_name?: string | null;
  screen_count?: string | null;
  help_with?: string | null;
}

interface CalendlyWidgetProps {
  buttonText?: string;
  buttonVariant?:
    | "primary"
    | "outlinePrimary"
    | "secondary"
    | "outlineSecondary"
    | "ghost"
    | "outlineDark"
    | "dark";
  buttonSize?: "sm" | "md" | "lg";
  buttonClassName?: string;
  calendlyUrl?: string;
  widgetHeight?: string;
  widgetMinWidth?: string;
  /** Return `false` to prevent opening Calendly (e.g. failed validation). Async returns are fire-and-forget. */
  onButtonClick?: () => boolean | void | Promise<void>;
  buttonTrackingId?: TrackingId;
  disabled?: boolean;
  autoPopulate?: CalendlyAutoPopulate;
  /** Controlled mode: when provided, the button is hidden and the modal open/close is driven by this prop */
  isOpen?: boolean;
  /** Called when the modal is closed in controlled mode */
  onClose?: () => void;
  /** Eagerly load the Calendly script (e.g. on contact form pages) */
  preload?: boolean;
}

/** Build initInlineWidget options with URL params + prefill.customAnswers (required for dropdown fields). */
export function buildCalendlyWidgetInitOptions(
  calendlyUrl: string,
  autoPopulate: CalendlyAutoPopulate,
  parentElement: HTMLElement,
  widgetHeight: string,
  widgetMinWidth: string
) {
  const customAnswers: Record<string, string> = {};

  /** talk-to-our-experts event field order:
   * a1 = Company Name, a2 = Phone, a4 = Screen count, a5 = What can we help you with?
   * Ref: https://help.calendly.com/hc/en-us/articles/223147027-How-to-pre-fill-invitee-information-in-an-embed
   */
  if (autoPopulate.company_name) customAnswers.a1 = autoPopulate.company_name;
  if (autoPopulate.phone) customAnswers.a2 = autoPopulate.phone;
  if (autoPopulate.screen_count) {
    customAnswers.a4 = mapScreenCountForCalendly(autoPopulate.screen_count);
  }
  if (autoPopulate.help_with) customAnswers.a5 = autoPopulate.help_with;

  const params = new URLSearchParams();
  if (autoPopulate.name) params.append("name", autoPopulate.name);
  if (autoPopulate.email) params.append("email", autoPopulate.email);
  Object.entries(customAnswers).forEach(([key, value]) => params.append(key, value));

  const separator = calendlyUrl.includes("?") ? "&" : "?";
  const url = params.toString() ? `${calendlyUrl}${separator}${params.toString()}` : calendlyUrl;

  return {
    url,
    parentElement,
    minWidth: widgetMinWidth,
    height: widgetHeight,
    prefill: {
      name: autoPopulate.name || undefined,
      email: autoPopulate.email || undefined,
      customAnswers,
    },
  };
}

export const CalendlyWidget: React.FC<CalendlyWidgetProps> = ({
  buttonText = "Schedule My Demo",
  buttonVariant = "outlinePrimary",
  buttonSize = "lg",
  buttonClassName = "",
  calendlyUrl = "https://calendly.com/d/dy6-mgh-mdz/book-a-free-demo-ask-our-experts-anything",
  widgetHeight = "700px",
  widgetMinWidth = "320px",
  onButtonClick,
  buttonTrackingId,
  disabled = false,
  autoPopulate,
  isOpen,
  onClose,
  preload = false,
}) => {
  const isControlled = isOpen !== undefined;
  const [isModalOpen, setIsModalOpen] = useState(isControlled ? isOpen : false);
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(globalCalendlyLoaded);
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = addCalendlyListener(() => {
      setIsCalendlyLoaded(true);
    });

    if (globalCalendlyLoaded) {
      setIsCalendlyLoaded(true);
    }

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isControlled) {
      setIsModalOpen(isOpen!);
    }
  }, [isControlled, isOpen]);

  const openCalendlyModal = () => {
    if (disabled) return;
    if (onButtonClick?.() === false) return;

    setIsModalOpen(true);

    window.dispatchEvent(
      new CustomEvent("mixpanelEvent", {
        detail: {
          type: "book-demo-submitted",
          data: {
            cta_location: buttonTrackingId || "Unknown",
            name: autoPopulate?.name || null,
            email: autoPopulate?.email || null,
            phone: autoPopulate?.phone || null,
            screen_count: autoPopulate?.screen_count || null,
            help_with: autoPopulate?.help_with || null,
          },
        },
      })
    );
  };

  const closeCalendlyModal = useCallback(() => {
    setIsModalOpen(false);
    onClose?.();
  }, [onClose]);

  const handleBackdropInteraction = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (
      event.type === "click" ||
      (event.type === "keydown" && (event as React.KeyboardEvent).key === "Enter")
    ) {
      closeCalendlyModal();
    }
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        closeCalendlyModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen, closeCalendlyModal]);

  useEffect(() => {
    if (!isModalOpen || !isCalendlyLoaded || typeof window === "undefined") return;
    if (isControlled && !autoPopulate) return;

    const calendlyWidget = widgetRef.current;
    if (!calendlyWidget || !(window as any).Calendly) return;

    calendlyWidget.innerHTML = "";

    const initOptions = autoPopulate
      ? buildCalendlyWidgetInitOptions(
          calendlyUrl,
          autoPopulate,
          calendlyWidget,
          widgetHeight,
          widgetMinWidth
        )
      : {
          url: calendlyUrl,
          parentElement: calendlyWidget,
          minWidth: widgetMinWidth,
          height: widgetHeight,
        };

    (window as any).Calendly.initInlineWidget(initOptions);
  }, [
    isModalOpen,
    isCalendlyLoaded,
    isControlled,
    calendlyUrl,
    widgetHeight,
    widgetMinWidth,
    autoPopulate,
  ]);

  return (
    <>
      {!globalCalendlyLoaded && (
        <Script
          src="https://assets.calendly.com/assets/external/widget.js"
          strategy={preload ? "afterInteractive" : "lazyOnload"}
          onLoad={() => {
            notifyCalendlyLoaded();
          }}
        />
      )}

      {!isControlled && (
        <NewButton
          variant={buttonVariant}
          size={buttonSize}
          className={buttonClassName}
          onClick={openCalendlyModal}
          disabled={disabled}
          {...(buttonTrackingId
            ? { "data-mixpanel-id": buttonTrackingId }
            : { "data-mixpanel-id": "cta-book-a-demo" })}
        >
          {buttonText}
        </NewButton>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={handleBackdropInteraction}
            onKeyDown={handleBackdropInteraction}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
          />

          <div className="relative rounded-lg shadow-xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <button
              onClick={closeCalendlyModal}
              className="fixed top-4 right-6 p-1 z-20 bg-white bg-opacity-90 hover:bg-opacity-100 border-none rounded-full w-10 h-10 flex justify-center text-xl cursor-pointer transition-all duration-200 shadow-lg"
              aria-label="Close modal"
              type="button"
            >
              ×
            </button>

            <div className="absolute inset-0 flex items-center justify-center z-0">
              <span className="text-white text-lg font-bold">Loading...</span>
            </div>

            <div
              ref={widgetRef}
              className="calendly-inline-widget z-10 translate-1 relative"
              style={{ minWidth: widgetMinWidth, height: widgetHeight }}
            />
          </div>
        </div>
      )}
    </>
  );
};
